// forkErrorLog — structured logger for the fork → snapshot → publish →
// skills pipeline.
//
// Why this exists: when a fork fails, the user's first instinct is to
// "report a bug" (we have a button for that), but the toast currently
// only carries the raw error message. Bug reports without the *step*,
// *resource ids*, *HTTP status*, and a correlatable *request id* are
// painful to triage — admins end up tailing edge logs blind.
//
// This helper:
//   1. Extracts whatever signal we can from the thrown error
//      (FunctionsHttpError.context.response, PostgrestError, plain
//      Error) — status, request id, supabase code.
//   2. Builds a JSON-serialisable record stamped with the step name,
//      cached resource ids (snapshot/publish), idempotency key and
//      attempt count.
//   3. Logs it via `console.error` with a stable `[fork-error]` prefix
//      so the existing `installConsoleCapture()` ring buffer + the
//      `report_error` RPC pick it up automatically — the admin
//      `/admin/errors` queue will see the structured record.
//   4. Returns a short *context tag* (eg `fork-pub:publish:abc123`)
//      that the toast can surface so the user has something concrete
//      to quote when they open a support thread.
//
// Kept dependency-free so we can call it from anywhere in the fork
// flow without dragging in supabase-js types at the use site.

/** The phases the fork pipeline can fail at. Mirrors the `step` var
 *  in ForkAndPublishButton so the log lines up with the on-screen
 *  toast copy. */
export type ForkStep = "snapshot" | "publish" | "skills";

export interface ForkErrorContext {
  /** Stable correlation id minted client-side at the start of the flow.
   *  Embedded in the toast and the reported log so user + admin can
   *  match on the same string. */
  flowId: string;
  /** Which phase blew up. */
  step: ForkStep;
  /** Cached resource ids — populated as each phase succeeds. */
  snapshotId?: string | null;
  publishedEntryId?: string | null;
  /** Source playbook slug or library entry id (whichever we have). */
  sourceRef?: string | null;
  /** Idempotency key used for the publish call when applicable. */
  idempotencyKey?: string | null;
  /** Total attempts the retry helper made before giving up. */
  attempts?: number;
  /** True when `isRetryableError` says the failure looked transient. */
  retryable?: boolean;
}

interface ExtractedHttp {
  status: number | null;
  /** `x-request-id` / `sb-request-id` header if the edge function
   *  emitted one — tells admins which exact invocation to grep for. */
  requestId: string | null;
  /** Supabase / Postgrest error code (eg `23505`, `PGRST116`). */
  code: string | null;
}

/** Pull whatever we can out of the thrown value. Handles three shapes:
 *  - `FunctionsHttpError` (supabase-js v2): has `.context.response`.
 *  - PostgREST error: object with `.code`, `.details`, `.message`.
 *  - Plain Error / string. */
const extractHttpSignal = (err: unknown): ExtractedHttp => {
  const out: ExtractedHttp = { status: null, requestId: null, code: null };
  if (!err || typeof err !== "object") return out;
  const e = err as {
    status?: number;
    statusCode?: number;
    code?: string | number;
    context?: { response?: Response };
    response?: Response;
  };
  if (typeof e.status === "number") out.status = e.status;
  else if (typeof e.statusCode === "number") out.status = e.statusCode;
  if (e.code != null) out.code = String(e.code);

  const resp: Response | undefined = e.context?.response ?? e.response;
  if (resp) {
    if (out.status == null && typeof resp.status === "number") {
      out.status = resp.status;
    }
    try {
      out.requestId =
        resp.headers.get("x-request-id") ||
        resp.headers.get("sb-request-id") ||
        resp.headers.get("cf-ray") ||
        null;
    } catch {
      // Headers can throw if the response was constructed weirdly.
    }
  }
  return out;
};

/** Mint a short, human-quotable id. Prefix lets logs filter
 *  cleanly; suffix is 8 hex chars from `crypto.randomUUID()`. */
export const newForkFlowId = (prefix = "fork"): string => {
  const suffix =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(16).slice(2, 10);
  return `${prefix}-${suffix}`;
};

export interface LoggedForkError {
  /** Short tag for the user-facing toast: `fork-pub:publish:abc12345`. */
  tag: string;
  /** Full structured record — also dumped to console for capture. */
  record: Record<string, unknown>;
}

/** Log a fork-pipeline failure and return a tag + record for downstream
 *  use (toast description, "Copy debug context" button, etc).
 *
 *  Always safe to call — never throws. */
export const logForkError = (
  err: unknown,
  ctx: ForkErrorContext,
): LoggedForkError => {
  const http = extractHttpSignal(err);
  const message =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : (() => {
            try {
              return JSON.stringify(err);
            } catch {
              return String(err);
            }
          })();
  const record: Record<string, unknown> = {
    flow_id: ctx.flowId,
    step: ctx.step,
    message: message.slice(0, 500),
    status: http.status,
    request_id: http.requestId,
    pg_code: http.code,
    snapshot_id: ctx.snapshotId ?? null,
    published_entry_id: ctx.publishedEntryId ?? null,
    source_ref: ctx.sourceRef ?? null,
    idempotency_key: ctx.idempotencyKey ?? null,
    attempts: ctx.attempts ?? null,
    retryable: ctx.retryable ?? null,
    at: new Date().toISOString(),
    url: typeof window !== "undefined" ? window.location.href : "",
  };

  // The `[fork-error]` prefix is what the admin error queue groups on.
  // We pass the original error as the second arg so the reporter
  // captures the stack alongside the structured payload.
  try {
    // eslint-disable-next-line no-console
    console.error(
      `[fork-error] ${ctx.step} ${ctx.flowId}`,
      record,
      err instanceof Error ? err : undefined,
    );
  } catch {
    // Logging must never crash the flow.
  }

  const tagSuffix = http.requestId
    ? http.requestId.slice(0, 8)
    : ctx.flowId.split("-").slice(-1)[0];
  return {
    tag: `${ctx.flowId}:${ctx.step}:${tagSuffix}`,
    record,
  };
};

/** Convenience formatter for the toast description so call sites stay
 *  terse. Produces eg: `step=publish · status=502 · req=abc12345`. */
export const formatForkErrorTag = (logged: LoggedForkError): string => {
  const r = logged.record as {
    step?: string;
    status?: number | null;
    request_id?: string | null;
    flow_id?: string;
  };
  const parts: string[] = [];
  if (r.step) parts.push(`step=${r.step}`);
  if (r.status) parts.push(`status=${r.status}`);
  if (r.request_id) parts.push(`req=${r.request_id.slice(0, 8)}`);
  else if (r.flow_id) parts.push(`flow=${r.flow_id}`);
  return parts.join(" · ");
};